import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { HmoResMock } from '../types/hmo.type'

type Props = {
  id?: string
  page?: number | string
  limit?: number | string
  query?: string
  sortDir?: 'asc' | 'desc'
}
export const useHmo = (arg?: Props) => {
  const id = arg?.id
  const query = arg?.query
  const limit = arg?.limit
  const page = arg?.page
  const sortDir = arg?.sortDir || 'asc'

  const { data: hmos, isLoading: hmoIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<HmoResMock[]>>(`hmos`, {
        params: {
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(query && { query }),
          ...(sortDir && { sort_dir: sortDir })
        }
      })
      return data
    },
    queryKey: ['hmos', { limit, page, query, sortDir }]
  })

  const { data: singleHmo, isLoading: singleHmoIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<HmoResMock>>(`hmos/${id}`)
      return data.data
    },
    queryKey: ['hmos', id]
  })

  const { mutateAsync: createHmo } = useMutation({
    mutationFn: async (formRec: any) => {
      const { data } = await AXIOS_INSTANCE.post('hmos', formRec, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
    }
  })

  const { mutateAsync: updateHmo } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`hmos/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
    }
  })

  const { mutateAsync: deleteHmo } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`hmos/${id}`, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
    }
  })

  return {
    createHmo,
    deleteHmo,
    hmoIsLoading,
    hmos: hmos?.data,
    pagination: hmos?.meta.pagination,
    singleHmo,
    singleHmoIsLoading,
    updateHmo
  }
}
